/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */
import React, { useEffect, useState } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import _ from 'underscore'
import Modal from '../../Modal/index'
import { useModal } from '../../../contexts/ModalContext'
import { useServiceContext } from '../../../root'
import * as packagesApi from '../../../services/api/packages'
import * as etmfApi from '../../../services/api/etmf'
import EditPackageStep1 from './EditPackageStep1'
import EditPackageStep2 from '../EditPackageStep2'
import EditPackageReview from './EditPackageReview'
import type { PackageData, PackageOptions, TriggerOption, SelectedFilters } from './types'
import { useNavigate } from 'react-router'
import ApplyPackageModal from '../apply-package/ApplyPackageModal'

export function parseTriggerNameOrRoles(
  isRoles: boolean,
  data: string[] | undefined,
  translate: (key: string) => string
): TriggerOption[] {
  if (isRoles) {
    return data?.length ? data.map((item) => ({ label: item, value: item })) : []
  } else {
    return data?.length ? data.map((item) => ({ label: translate(`TRIGGER_${item}`), value: item })) : []
  }
}

interface EditPackageModalProps {
  id?: number
  isClone?: boolean
  isFromPkgActivity?: boolean
  onClose?: (packageData: any) => void
}

const EditPackageModal: React.FC<EditPackageModalProps> = ({ id, isClone, isFromPkgActivity, onClose }) => {
  const { hideModal, showModal } = useModal()
  const context = useServiceContext()
  const navigate = useNavigate()
  const { constant, service } = context
  const { translate } = service.filter
  const { eventTriggers } = context?.service?.init?.getFeaturesInfo?.() || {}
  const [isOpen, setIsOpen] = useState(true)
  const [packageData, setPackageData] = useState<PackageData | null>(null)
  const [filePlans, setFilePlans] = useState<any[] | null>(null)
  const [packagesForPicker, setPackagesForPicker] = useState<any[] | null>(null)
  const [currentStep, setCurrentStep] = useState<'STEP1' | 'STEP2' | 'STEP3'>('STEP1')
  const [options, setOptions] = useState<PackageOptions | null>(null)
  const [triggersNames, setTriggersName] = useState<TriggerOption[]>([])
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    rule: [],
    stage: [],
    milestone: []
  })
  const [manuallySelectedArtifactIds, setManuallySelectedArtifactIds] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const promise = [etmfApi.getFilePlansForPicker(null), packagesApi.getAllForPicker()]
    if (eventTriggers?.length) {
      packagesApi.getTriggerList().then((triggerNames) => {
        // API returns string[] directly, not wrapped in an object
        setTriggersName(parseTriggerNameOrRoles(false, triggerNames, translate))
      })
    }

    Promise.all(promise).then((response) => {
      setFilePlans(response[0]?.elements)
      setPackagesForPicker(response[1])
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    setOptions({
      isEdit: _.isNumber(id),
      filePlans,
      packagesForPicker,
      packageCopy: cloneDeep(packageData),
      isClone
    })
  }, [filePlans, packagesForPicker])

  useEffect(() => {
    setOptions((data) => ({ ...data, packageCopy: cloneDeep(packageData) }))
  }, [packageData])

  useEffect(() => {
    if (id) {
      packagesApi.get(String(id)).then((data) => {
        const pckgData: PackageData = data as any
        pckgData.entryIds = _.pluck(pckgData.entries as any, 'id')
        pckgData.entryNames = _.pluck(pckgData.entries as any, 'name')
        pckgData.preselectedArtifactIds = new Set()
        delete pckgData.entries
        if (isClone) {
          pckgData.name += ' - CLONE'
        }
        setPackageData(pckgData)
      })
    } else {
      setPackageData({
        level: constant.etmf.level.STUDY,
        preselectedArtifactIds: new Set()
      })
    }
  }, [id])

  const doSave = (applyPackage: boolean) => {
    if (applyPackage) {
      saveAndApply()
    } else {
      save()
    }
  }

  const save = () => {
    //
    const dto: any = cloneDeep(
      _.omit(packageData, 'id', 'entries', 'entryIds', 'preselectedArtifactIds', 'preselectionDone')
    )
    dto.entryIds = _.pluck(options?.selectedFPEntries as any, 'id')
    if (eventTriggers?.length && packageData?.autoApplyPackage) {
      dto.autoApplyPackage = packageData?.autoApplyPackage || false
      dto.triggerName =
        typeof packageData?.triggerName === 'object' && packageData?.triggerName !== null
          ? packageData.triggerName.value
          : packageData?.triggerName
      dto.triggerDetails =
        Array.isArray(packageData?.triggerDetails) && packageData.triggerDetails?.length
          ? _.pluck(packageData?.triggerDetails, 'value')
          : packageData?.triggerDetails
            ? [packageData.triggerDetails]
            : []
    }

    const apiCall =
      options?.isEdit && !options?.isClone
        ? packagesApi.updatePackage(String(packageData?.id), dto)
        : packagesApi.createPackage(dto)

    apiCall.then(
      function () {
        doClose(packageData)
        service.toast.success(
          options?.isEdit && !options?.isClone
            ? 'SETTINGS_PACKAGE_EDITED_SUCCESS_MSG'
            : 'SETTINGS_PACKAGE_ADDED_SUCCESS_MSG'
        )
        if (isFromPkgActivity) {
          navigate(`/reports/activity?type=PACKAGES&id=${packageData?.id}&itemName=${packageData?.name}`)
        }
      },
      function (data: any) {
        if (data?.message) {
          service.toast.error(data.message)
        } else {
          service.toast.error('ERROR')
        }
      }
    )

    return apiCall
  }

  const saveAndApply = () => {
    save().then((resp) => {
      const pckg = resp as any
      if (pckg?.id) {
        showModal(<ApplyPackageModal id={pckg.id} />)
        service.toast.info('PACKAGE_SAVED_APPLY_SKIPPED')
      } else {
        service.toast.error('ERROR')
      }
    })
  }

  const doClose = (packageData: any) => {
    setIsOpen(false)
    onClose && onClose(packageData)
    hideModal()
  }

  const nextStep = (
    packageData: PackageData,
    optionsData: PackageOptions,
    nextStepItem: 'STEP1' | 'STEP2' | 'STEP3'
  ) => {
    optionsData.packageCopy = cloneDeep(packageData)
    setPackageData(packageData)
    setOptions(optionsData)
    setCurrentStep(nextStepItem)
  }

  const getStep = () => {
    switch (currentStep) {
      case 'STEP1':
        return (
          <EditPackageStep1
            packageData={packageData}
            options={options}
            nextStep={(packageData, optionsData, nextStepItem) => nextStep(packageData, optionsData, nextStepItem)}
            doClose={() => doClose(packageData)}
            setSelectedFilters={setSelectedFilters}
            triggersNames={triggersNames}
            isLoading={isLoading}
          />
        )
      case 'STEP2':
        return (
          <EditPackageStep2
            packageData={packageData}
            setPackageData={setPackageData}
            options={options as any}
            nextStep={(packageData, optionsData, nextStepItem) =>
              nextStep(packageData, optionsData, nextStepItem as any)
            }
            doClose={() => doClose(packageData)}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            manuallySelectedArtifactIds={manuallySelectedArtifactIds}
            setManuallySelectedArtifactIds={setManuallySelectedArtifactIds}
          />
        )
      case 'STEP3':
        return (
          <EditPackageReview
            packageData={packageData}
            options={options}
            nextStep={(packageData, optionsData, nextStepItem) => nextStep(packageData, optionsData, nextStepItem)}
            doSave={doSave}
            doClose={() => doClose(packageData)}
          />
        )
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={() => doClose(packageData)}
      className="theme-sandman edit-package-modal-wrapper"
      title={translate(
        options?.isEdit
          ? options?.isClone
            ? 'SETTINGS_PACKAGE_CLONE_TITLE'
            : 'SETTINGS_PACKAGE_EDIT_TITLE'
          : 'SETTINGS_PACKAGE_ADD_TITLE'
      )}
      size="lg"
      style={{ flex: 1, overflow: 'visible' }}
    >
      <div className="edit-packages-wrapper">{getStep()}</div>
    </Modal>
  )
}

export default EditPackageModal
